import type { MetaFunction } from "@remix-run/cloudflare";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { DownloadButton } from "~/components/ui/download-button";
import { ProtectedDownloadLink } from "~/components/ui/protected-download-link";
import { 
  Download, 
  Eye, 
  Heart, 
  Calendar,
  User,
  Shield,
  Star,
  FileText,
  BarChart3,
  MessageSquare,
  Clock,
  CheckCircle,
  Palette,
  Image,
  Zap
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Minervamon - Digimon Outfit Mod | stellarblademod.com" },
    { 
      name: "description", 
      content: "Minervamon mod for Stellar Blade - Transform the Fluffy Bear outfit into Minervamon from Digimon with complete visual overhaul." 
    },
    { name: "keywords", content: "stellar blade minervamon, stellar blade digimon mod, fluffy bear outfit, minervamon outfit, digimon character mod" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/mods/minervamon" },
    
    // Open Graph
    { property: "og:title", content: "Minervamon - Digimon Outfit Mod" },
    { property: "og:description", content: "Transform the Fluffy Bear outfit into Minervamon from Digimon with complete visual transformation." },
    { property: "og:type", content: "article" },
    { property: "og:url", content: "https://stellarblademod.com/mods/minervamon" },
    { property: "og:image", content: "https://stellarblademod.com/mods/minervamon1.webp" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Minervamon - Digimon Outfit Mod" },
    { name: "twitter:description", content: "Transform the Fluffy Bear outfit into Minervamon from Digimon with complete visual transformation." },
    { name: "twitter:image", content: "https://stellarblademod.com/mods/minervamon1.webp" },
  ];
};

export default function Minervamon() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div 
        className="relative text-white py-12 border-b border-slate-800"
        style={{
          backgroundImage: "url('/mods/minervamon1.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Mod Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Badge variant="secondary" className="bg-cyan-500/20 text-cyan-300 border-cyan-400/30">
                  Outfits
                </Badge>
                <Badge variant="secondary" className="bg-purple-500/20 text-purple-300 border-purple-400/30">
                  Digimon Crossover
                </Badge>
              </div>
              
              <h1 className="text-4xl font-bold mb-4">
                Minervamon
              </h1>
              
              <p className="text-xl opacity-90 mb-6">
                Transform the Fluffy Bear outfit into Minervamon from Digimon. Experience EVE with this unique crossover character design featuring distinctive armor and weapon styling.
              </p>
              
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Created by <a href="https://www.nexusmods.com/users/155348093" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Hallow</a></span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Updated: June 9, 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span>Version 1.0</span>
                </div>
              </div>
            </div>
            
            {/* Stats Card */}
            <div className="lg:col-span-1">
              <Card className="bg-slate-800/50 border-slate-700 text-white backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-cyan-300">
                    <BarChart3 className="h-5 w-5" />
                    Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Endorsements
                    </span>
                    <span className="font-bold">31</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Downloads
                    </span>
                    <span className="font-bold">1,247</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Views
                    </span>
                    <span className="font-bold">3,892</span>
                  </div>
                  <Separator className="bg-slate-600" />
                  <DownloadButton className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white border-0 shadow-lg" scrollOnly>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </DownloadButton>
                  <p className="text-xs opacity-80 text-center">
                    Manual download available
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-cyan-300">About this mod</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-slate max-w-none text-slate-300">
                <div className="space-y-6">
                  {/* Overview */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Overview:</h3>
                    <div className="bg-slate-800/30 border border-slate-600 p-4 rounded-lg backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        The Minervamon mod brings the iconic Digimon character to Stellar Blade by completely transforming the Fluffy Bear outfit. This mod replaces the original outfit with Minervamon's distinctive armor design, creating a unique crossover experience.
                      </p>
                      <p className="text-sm mb-3">
                        Minervamon is known for her elegant yet powerful appearance in the Digimon universe, and this mod captures that essence perfectly within Stellar Blade's aesthetic. Players can experience the game with this unique character design while maintaining all original gameplay functionality.
                      </p>
                      <p className="text-sm">
                        <strong>Important Note:</strong> This mod replaces the Fluffy Bear outfit specifically. Some hair physics may behave unexpectedly and can be quite amusing during gameplay.
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Features */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Key Features:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <Palette className="h-4 w-4 mt-1 text-purple-600" />
                        <span><strong>Complete Outfit Transformation:</strong> Fluffy Bear outfit replaced with Minervamon's iconic armor design</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Image className="h-4 w-4 mt-1 text-cyan-600" />
                        <span><strong>Authentic Digimon Styling:</strong> Faithful recreation of Minervamon's distinctive appearance</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Zap className="h-4 w-4 mt-1 text-yellow-600" />
                        <span><strong>Unique Hair Physics:</strong> Dynamic hair effects that add character to gameplay (with amusing quirks)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-1 text-green-600" />
                        <span><strong>Full Game Compatibility:</strong> Works seamlessly with all game modes and story progression</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Star className="h-4 w-4 mt-1 text-orange-600" />
                        <span><strong>Crossover Appeal:</strong> Perfect for fans of both Stellar Blade and Digimon franchises</span>
                      </li>
                    </ul>
                  </div>

                  <Separator />

                  {/* Installation */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Installation Guide:</h3>
                    <ol className="space-y-2 list-decimal list-inside">
                      <li><strong>Download</strong> the Minervamon mod file (.7z archive)</li>
                      <li><strong>Create a ~mods folder</strong> in your game's Paks directory: <code className="bg-slate-700 text-cyan-300 px-1 rounded">StellarBlade/SB/Content/Paks/~mods</code></li>
                      <li><strong>Extract</strong> all .pak files from the .7z archive to the ~mods folder</li>
                      <li><strong>Launch the game</strong> and equip the Fluffy Bear outfit to see the transformation</li>
                      <li><strong>Enjoy</strong> playing as Minervamon in Stellar Blade!</li>
                    </ol>
                  </div>

                  <Separator />

                  {/* Known Issues */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Known Issues & Notes:</h3>
                    <div className="space-y-4">
                      <div className="bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-yellow-300">Ponytail Removal</h4>
                        <p className="text-sm">
                          The mod author is currently unsure how to remove the ponytail or head accessories without affecting other outfits. This may cause visual overlap in some scenarios.
                        </p>
                      </div>
                      
                      <div className="bg-blue-900/20 border-l-4 border-blue-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-blue-300">Hair Physics Quirks</h4>
                        <p className="text-sm">
                          Hair physics are described as "wacky" by the mod author, but this actually adds a fun and amusing element to the gameplay experience. The physics are functional but may behave unexpectedly during combat or movement.
                        </p>
                      </div>

                      <div className="bg-green-900/20 border-l-4 border-green-400 p-4 rounded backdrop-blur-sm">
                        <h4 className="font-semibold mb-2 text-green-300">Simple Installation</h4>
                        <p className="text-sm">
                          Installation is straightforward - simply unzip the contents into your Paks folder. No complex configuration or additional steps required.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Player Experience */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-cyan-300">Player Experience:</h3>
                    <div className="bg-slate-800/30 border border-slate-600 p-4 rounded-lg backdrop-blur-sm">
                      <p className="text-sm mb-3">
                        This crossover mod offers a unique and entertaining experience for players familiar with both franchises. The visual transformation is striking and provides a fresh perspective on Stellar Blade's combat system.
                      </p>
                      <p className="text-sm mb-3">
                        The Minervamon design translates surprisingly well to Stellar Blade's aesthetic, maintaining the elegance and power that the character is known for in Digimon media. The armor design complements the game's visual style while standing out as distinctly different.
                      </p>
                      <p className="text-sm">
                        Players particularly enjoy the novelty of playing through Stellar Blade's intense combat scenarios while embodying this iconic Digimon character. The amusing hair physics add an unexpected element of fun to the experience.
                      </p>
                    </div>
                  </div>

                  {/* Support Links */}
                  <div className="flex flex-wrap gap-4 pt-6">
                    <DownloadButton 
                      variant="outline" 
                      className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-800"
                      scrollOnly
                    >
                      <Download className="h-4 w-4" />
                      Download Latest Version
                    </DownloadButton>
                    <Button variant="outline" asChild>
                      <a href="/contact" className="flex items-center gap-2 border-slate-600 text-slate-300 hover:bg-slate-800">
                        <MessageSquare className="h-4 w-4" />
                        Community Support
                      </a>
                    </Button>
                  </div>

                  <Separator />

                  {/* Original Page Link */}
                  <div className="mt-8 p-6 bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border border-slate-600 rounded-lg backdrop-blur-sm">
                    <h4 className="text-lg font-semibold mb-3 text-cyan-300">Visit Original Page</h4>
                    <p className="text-sm text-slate-300 mb-4">
                      Check the official Nexus Mods page for latest updates, community discussions, and additional images.
                    </p>
                    <Button asChild className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white border-0 shadow-lg">
                      <a 
                        href="https://www.nexusmods.com/stellarblade/mods/155" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <Star className="h-4 w-4" />
                        View Nexus Mods Page
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* File Information */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">File Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-slate-300">
                  <div className="flex justify-between text-sm">
                    <span>Last updated</span>
                    <span>June 9, 2025</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Original upload</span>
                    <span>June 9, 2025</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Created by</span>
                    <a href="https://www.nexusmods.com/users/155348093" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">Hallow</a>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Uploaded by</span>
                    <span>HallowSL</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Virus scan</span>
                    <Badge variant="secondary" className="bg-green-900/50 text-green-300 border-green-600">
                      Safe to use
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Download Section */}
              <Card id="download-section" className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Download Files</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* File Information */}
                    <div className="p-4 border border-slate-600 rounded-lg">
                      <h4 className="font-semibold text-slate-200">Minervamon</h4>
                      <p className="text-sm text-slate-400">Digimon crossover outfit transformation</p>
                      <p className="text-xs text-slate-500">Version 1.0 • 42.3 MB</p>
                    </div>
                    
                    {/* Download Button */}
                    <div className="w-full">
                      <ProtectedDownloadLink 
                        href="https://r2.stellarblademod.com/Minervamon-155-1-0-1749430280.7z" 
                        className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </ProtectedDownloadLink>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="border-slate-600 text-slate-300">digimon</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">minervamon</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">outfit</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">crossover</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-300">fluffy-bear</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Permissions */}
              <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-cyan-300">Permissions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-xs text-slate-400">
                    <p><strong className="text-slate-300">Upload permission:</strong> Not allowed</p>
                    <p><strong className="text-slate-300">Modification permission:</strong> Permission required</p>
                    <p><strong className="text-slate-300">Conversion permission:</strong> Not allowed</p>
                    <p><strong className="text-slate-300">Asset use permission:</strong> Permission required</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 